import { Component, inject, OnInit, Signal, ViewChild, computed, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrganizationListComponent } from '../components/organization-list/organization-list.component';
import { OrganizationFacade } from '../../../shared/state/organization/facades/organization.facade';
import { OrganizationService } from '../../../shared/services/organization.service';
import { IOrganization, IOrganizationDetail } from '../../../shared/models/organization';
import { FontAwesomeSharedModule } from '../../../shared/font-awesome/font-awesome';
import { AddOrganizationModalComponent } from '../modals/add-organization-modal/add-organization-modal.component';
import { DeleteOrganizationConfirmationModalComponent } from '../modals/delete-organization-confirmation/delete-organization-confirmation-modal.component';
import { DeleteOrganizationWarningModalComponent } from '../modals/delete-organization-warning/delete-organization-warning-modal.component';
import { ConfigureGuidelinesModalComponent } from '../modals/configure-guidelines-modal/configure-guidelines-modal.component';
import {
  ADD_ORGANIZATION_MODAL_ID,
  DELETE_ORGANIZATION_MODAL_ID,
  DELETE_ORGANIZATION_WARNING_MODAL_ID,
} from '../constants/organization-constants';
import { LoggerService } from '../../../shared/services/logger.service';
import { DialogService } from '../../../shared/services/dialog.service';

@Component({
  selector: 'app-organization-management-container',
  standalone: true,
  imports: [
    OrganizationListComponent,
    FormsModule,
    CommonModule,
    FontAwesomeSharedModule,
    AddOrganizationModalComponent,
    DeleteOrganizationConfirmationModalComponent,
    DeleteOrganizationWarningModalComponent,
    ConfigureGuidelinesModalComponent,
  ],
  templateUrl: './organization-management.container.component.html',
})
export class OrganizationManagementContainerComponent implements OnInit {
  private organizationFacade = inject(OrganizationFacade);
  private organizationService = inject(OrganizationService);
  private logger = inject(LoggerService);
  private dialogService = inject(DialogService);
  activeFilter: WritableSignal<string> = signal('active');
  organizationListSignal: Signal<IOrganization[]> = this.organizationFacade.organizationListSignal;
  selectedOrganizationId: number | null = null;
  selectedOrganization: IOrganization | null = null;

  @ViewChild(ConfigureGuidelinesModalComponent) guidelinesModal!: ConfigureGuidelinesModalComponent;

  totalOrganizationsCount = computed(() => {
    return this.organizationListSignal().length;
  });

  ngOnInit(): void {
    // Initial load - explicitly fetch active organizations
    this.organizationFacade.getOrganizationList(true);
  }

  onActiveFilterChange(filterValue: string): void {
    this.activeFilter.set(filterValue);
    const active = filterValue === 'active' ? true : filterValue === 'deleted' ? false : undefined;
    this.organizationFacade.getOrganizationList(active);
  }

  addOrganizationModal() {
    this.dialogService.open(ADD_ORGANIZATION_MODAL_ID);
  }

  onOrganizationSelected(organizationId: number) {
    this.selectedOrganizationId = organizationId;
    this.selectedOrganization = this.organizationListSignal().find((org) => org.id === organizationId) || null;

    if (this.selectedOrganizationId) {
      // Fetch organization details to check if it has users
      this.organizationService.getOrganizationDetail(organizationId).subscribe({
        next: (detail: IOrganizationDetail) => {
          // Check if organization has users using the has_users field
          if (detail.has_users === true) {
            // Show warning modal - cannot delete
            this.dialogService.open(DELETE_ORGANIZATION_WARNING_MODAL_ID);
          } else {
            // Show confirmation modal - can delete
            this.dialogService.open(DELETE_ORGANIZATION_MODAL_ID);
          }
        },
        error: (error) => {
          this.logger.error('Error fetching organization details', 'OrganizationManagementContainerComponent', error);
          // On error, show confirmation modal as fallback
          this.dialogService.open(DELETE_ORGANIZATION_MODAL_ID);
        },
      });
    }
  }

  onConfigureGuidelines(organization: IOrganization) {
    this.guidelinesModal.open(organization);
  }

  onDeleteConfirmed() {
    if (this.selectedOrganizationId) {
      this.organizationFacade.deleteOrganization(this.selectedOrganizationId);
      this.selectedOrganizationId = null;
      this.selectedOrganization = null;
    }
  }
}
